// Derived/computed lifecycle values. Pure functions over LifeState — never
// stored, always recomputed, so they update reactively whenever data changes.

import type { LifeState, Quadrant, VisionCondition } from './types.js';

export type LifeComputed = {
  totalAssetValue: number; // Σ asset.value
  totalLiabilityValue: number; // Σ liability.value
  netWorth: number; // assets − liabilities (value)
  totalWealth: number; // netWorth + liquid cash — "how rich am I, really"
  passiveIncome: number; // Σ asset.monthlyIncome  (money in, without working)
  monthlyExpenses: number; // Σ liability.monthlyCost + Σ asset.monthlyCost + livingCosts
  freedomScore: number; // passiveIncome / monthlyExpenses  (ratio)
  freedomPct: number; // freedomScore as a clamped 0..∞ percentage (rounded)
  freedomMode: boolean; // passive income covers expenses → freedom unlocked
  mindsetAverage: number; // mean of the five sub-stats (0..100)
  suggestedQuadrant: Quadrant; // where the player effectively sits, from income mix
};

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export function deriveLife(s: LifeState): LifeComputed {
  const totalAssetValue = sum(s.assets.map((a) => a.value));
  const totalLiabilityValue = sum(s.liabilities.map((l) => l.value));
  const netWorth = totalAssetValue - totalLiabilityValue;

  // Spec §6: passive income = sum of all asset monthly incomes.
  const passiveIncome = sum(s.assets.map((a) => a.monthlyIncome));
  // Spec §7: monthly expenses = liability monthly costs + living costs. We also
  // fold in any asset carrying costs (the spec gives assets a cost field) so the
  // figure is financially honest.
  const monthlyExpenses =
    sum(s.liabilities.map((l) => l.monthlyCost)) + sum(s.assets.map((a) => a.monthlyCost)) + s.livingCosts;

  const freedomScore = monthlyExpenses > 0 ? passiveIncome / monthlyExpenses : passiveIncome > 0 ? Infinity : 0;
  const freedomPct = freedomScore === Infinity ? 100 : Math.round(freedomScore * 100);
  const freedomMode = passiveIncome >= monthlyExpenses && passiveIncome > 0;

  const m = s.mindset;
  const mindsetAverage = Math.round((m.desire + m.faith + m.persistence + m.focus + m.gratitude) / 5);

  return {
    totalAssetValue,
    totalLiabilityValue,
    netWorth,
    totalWealth: netWorth + s.cash,
    passiveIncome,
    monthlyExpenses,
    freedomScore,
    freedomPct,
    freedomMode,
    mindsetAverage,
    suggestedQuadrant: suggestQuadrant(s),
  };
}

/** Which life-stage bracket an age falls in (1..4). Boundaries at 11, 14, 17 —
 *  crossing one re-opens the Vision Board and triggers a transition milestone. */
export function visionBracket(age: number): number {
  if (age <= 10) return 1; // 8–10
  if (age <= 13) return 2; // 11–13  (Explorer — Mastermind unlocks)
  if (age <= 16) return 3; // 14–16  (Builder)
  return 4; // 17–18  (Founder)
}

/** A friendly name for each bracket. */
export function bracketName(bracket: number): string {
  return ['', 'Explorer', 'Explorer', 'Builder', 'Founder'][bracket] ?? 'Explorer';
}

/** Is a vision item's condition met, given the current state + computed values? */
export function isVisionAchieved(cond: VisionCondition, state: LifeState, c: LifeComputed): boolean {
  switch (cond.kind) {
    case 'netWorth':
      return c.netWorth >= cond.target;
    case 'passiveIncome':
      return c.passiveIncome >= cond.target;
    case 'freedomPct':
      return c.freedomPct >= cond.target;
    case 'cash':
      return state.cash >= cond.target;
    case 'ownAssetType':
      return state.assets.some((a) => a.type === cond.assetType);
    case 'mindsetAvg':
      return c.mindsetAverage >= cond.target;
    default:
      return false;
  }
}

/**
 * Where the player effectively sits on the cashflow quadrant, inferred from how
 * they make money. Teaches that the goal is to move E/S → B/I (own systems and
 * investments that pay you).
 *   I — passive income already covers expenses (investments work for you)
 *   B — owns income-producing businesses (assets of type 'business')
 *   S — owns income assets but they don't yet cover life
 *   E — no income-producing assets yet
 */
export function suggestQuadrant(s: LifeState): Quadrant {
  const passive = sum(s.assets.map((a) => a.monthlyIncome));
  const expenses = sum(s.liabilities.map((l) => l.monthlyCost)) + sum(s.assets.map((a) => a.monthlyCost)) + s.livingCosts;
  if (passive > 0 && passive >= expenses) return 'I';
  const ownsBusiness = s.assets.some((a) => a.type === 'business');
  if (ownsBusiness) return 'B';
  if (s.assets.some((a) => a.monthlyIncome > 0)) return 'S';
  return 'E';
}
